import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { Trash2, X } from "lucide-react";
import api from "@/lib/api";
import {
  PageHeader, Card, Table, SecondaryButton, Field, Select, SortableHeader, useTableSort,
} from "@/components/admin/AdminUI";
import { formatDate } from "@/lib/format";
import { ModalScroll } from "@/components/public/ModalScroll";

const SEVERITY_STYLES = {
  error: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
};
const SOURCE_LABELS = {
  client: "Client JS",
  server: "Server 500",
  toast: "Toast",
  xhr: "XHR/API",
  promise: "Promise",
  console: "Console",
};

function Badge({ value, kind }) {
  const styles = kind === "severity"
    ? SEVERITY_STYLES[value] || "bg-gray-100 text-gray-700 border-gray-200"
    : "bg-[#F1F3F5] text-[#212529] border-[#E9ECEF]";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${styles}`}>
      {kind === "source" ? (SOURCE_LABELS[value] || value) : value}
    </span>
  );
}

export default function AdminErrorLog() {
  const qc = useQueryClient();
  const [severity, setSeverity] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState("");
  const [q, setQ] = useState("");
  const [sinceHours, setSinceHours] = useState("");
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { sort, toggleSort, sortRows } = useTableSort({ key: "created_at", dir: "desc" });

  const { data = { total: 0, items: [] }, isLoading } = useQuery({
    queryKey: ["admin-errors", { severity, source, page, q, sinceHours }],
    queryFn: async () => (await api.get("/admin/errors", {
      params: {
        severity: severity || undefined,
        source: source || undefined,
        page: page || undefined,
        q: q || undefined,
        since_hours: sinceHours || undefined,
        limit: 500,
      },
    })).data,
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-errors-stats"],
    queryFn: async () => (await api.get("/admin/errors/stats", { params: { since_hours: 24 } })).data,
  });

  const sorted = sortRows(data.items, (r, key) => {
    if (key === "created_at") return r.created_at ? new Date(r.created_at).getTime() : null;
    if (key === "http_status") return r.http_status ?? null;
    return r[key] ?? null;
  });

  async function clearAll() {
    const scope = [severity && `severity=${severity}`, source && `source=${source}`].filter(Boolean).join(", ") || "ALL";
    if (!window.confirm(`Delete ${data.total} error log entr${data.total === 1 ? "y" : "ies"} (${scope})?\n\nThis cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data: r } = await api.delete("/admin/errors", {
        params: { severity: severity || undefined, source: source || undefined },
      });
      toast.success(`Cleared ${r.deleted} entr${r.deleted === 1 ? "y" : "ies"}.`);
      qc.invalidateQueries({ queryKey: ["admin-errors"] });
      qc.invalidateQueries({ queryKey: ["admin-errors-stats"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    } finally {
      setDeleting(false);
    }
  }

  async function deleteOne(id) {
    try {
      await api.delete(`/admin/errors/${id}`);
      qc.invalidateQueries({ queryKey: ["admin-errors"] });
      qc.invalidateQueries({ queryKey: ["admin-errors-stats"] });
      setOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div data-testid="admin-error-log">
      <PageHeader
        title="Error log"
        subtitle={
          stats
            ? `${data.total} matching · last 24h: ${stats.total_in_window} (${Object.entries(stats.by_severity).map(([k, v]) => `${v} ${k}`).join(" · ") || "none"})`
            : `${data.total} matching`
        }
        actions={
          data.total > 0 && (
            <SecondaryButton
              onClick={clearAll}
              disabled={deleting}
              data-testid="clear-errors-button"
              className="text-clay hover:text-clay/80 border-clay/30"
            >
              <Trash2 className="w-4 h-4 inline mr-1" />
              {deleting ? "Clearing…" : "Clear matching"}
            </SecondaryButton>
          )
        }
      />

      <Card className="mb-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Search message">
            <input
              className="w-full h-10 px-3 border border-[#E9ECEF] rounded-md text-sm"
              defaultValue={q}
              onBlur={(e) => setQ(e.target.value.trim())}
              placeholder="Substring match…"
              data-testid="error-search"
            />
          </Field>
          <Field label="Severity">
            <Select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="error-severity-filter">
              <option value="">All</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </Select>
          </Field>
          <Field label="Source">
            <Select value={source} onChange={(e) => setSource(e.target.value)} data-testid="error-source-filter">
              <option value="">All</option>
              <option value="client">Client JS</option>
              <option value="server">Server 500</option>
              <option value="toast">Toast</option>
              <option value="xhr">XHR / API</option>
              <option value="promise">Promise</option>
            </Select>
          </Field>
          <Field label="Page (path contains)">
            <input
              className="w-full h-10 px-3 border border-[#E9ECEF] rounded-md text-sm"
              defaultValue={page}
              onBlur={(e) => setPage(e.target.value.trim())}
              placeholder="/admin/brands"
              data-testid="error-page-filter"
            />
          </Field>
          <Field label="Since">
            <Select value={sinceHours} onChange={(e) => setSinceHours(e.target.value)} data-testid="error-recency-filter">
              <option value="">All time</option>
              <option value="1">Last 1 hour</option>
              <option value="24">Last 24 hours</option>
              <option value="168">Last 7 days</option>
              <option value="720">Last 30 days</option>
            </Select>
          </Field>
        </div>
      </Card>

      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="When" sortKey="created_at" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Severity" sortKey="severity" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Source" sortKey="source" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Message" sortKey="message" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Page" sortKey="page" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="http_status" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr
                key={r.id}
                onClick={() => setOpen(r)}
                className="border-b border-[#E9ECEF] cursor-pointer hover:bg-[#F8F9FA]"
                data-testid={`error-row-${r.id}`}
              >
                <td className="px-3 py-3 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
                <td className="px-3 py-3"><Badge value={r.severity} kind="severity" /></td>
                <td className="px-3 py-3"><Badge value={r.source} kind="source" /></td>
                <td className="px-3 py-3 text-xs max-w-md">
                  <div className="truncate" title={r.message}>{r.message}</div>
                </td>
                <td className="px-3 py-3 text-xs text-[#6C757D] max-w-[200px] truncate" title={r.page}>{r.page || "—"}</td>
                <td className="px-3 py-3 text-xs">{r.http_status || "—"}</td>
              </tr>
            ))}
            {!isLoading && sorted.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-[#6C757D]" data-testid="error-empty-state">No errors logged.</td></tr>
            )}
          </tbody>
        </Table>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}
          data-testid="error-dialog"
        >
          <div className="bg-white border border-line shadow-2xl max-w-3xl w-full h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 pt-5 pb-3 border-b border-[#E9ECEF]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge value={open.severity} kind="severity" />
                    <Badge value={open.source} kind="source" />
                    {open.http_status && <span className="text-xs text-[#6C757D]">HTTP {open.http_status}</span>}
                  </div>
                  <h2 className="font-mono text-sm break-words">{open.message}</h2>
                  <div className="text-xs text-[#6C757D] mt-2">
                    {formatDate(open.created_at)} · {open.page || "(no page)"} {open.is_admin ? "· admin" : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(null)}
                  className="text-[#6C757D] hover:text-ink p-1"
                  aria-label="Close"
                  data-testid="error-dialog-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <ModalScroll className="px-6 py-4">
              {open.stack && (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Stack trace</div>
                  <pre className="text-[11px] whitespace-pre-wrap font-mono bg-[#F8F9FA] border border-[#E9ECEF] p-3 max-h-[400px] overflow-auto">{open.stack}</pre>
                </div>
              )}
              {open.endpoint && (
                <div className="mb-3 text-xs">
                  <span className="text-[#6C757D] uppercase tracking-wider text-[10px] mr-2">Endpoint</span>
                  <span className="font-mono">{open.http_method || "GET"} {open.endpoint}</span>
                </div>
              )}
              {open.url && (
                <div className="mb-3 text-xs">
                  <span className="text-[#6C757D] uppercase tracking-wider text-[10px] mr-2">URL</span>
                  <a href={open.url} target="_blank" rel="noopener noreferrer" className="font-mono text-forest underline break-all">{open.url}</a>
                </div>
              )}
              {open.user_agent && (
                <div className="mb-3 text-xs">
                  <span className="text-[#6C757D] uppercase tracking-wider text-[10px] mr-2">User-Agent</span>
                  <span className="font-mono break-all">{open.user_agent}</span>
                </div>
              )}
              {(open.visitor_id || open.session_id) && (
                <div className="mb-3 text-xs">
                  <span className="text-[#6C757D] uppercase tracking-wider text-[10px] mr-2">Visitor / session</span>
                  <span className="font-mono">{open.visitor_id || "—"} / {open.session_id || "—"}</span>
                </div>
              )}
              {open.extra && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Extra</div>
                  <pre className="text-[11px] whitespace-pre-wrap font-mono bg-[#F8F9FA] border border-[#E9ECEF] p-3">{JSON.stringify(open.extra, null, 2)}</pre>
                </div>
              )}
            </ModalScroll>

            <div className="border-t border-[#E9ECEF] px-6 py-3 flex justify-end gap-2">
              <SecondaryButton
                onClick={() => deleteOne(open.id)}
                className="text-clay hover:text-clay/80 border-clay/30"
                data-testid="delete-single-error"
              >
                <Trash2 className="w-4 h-4 inline mr-1" /> Delete this entry
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
